// Copyright (c) Docugami, Inc. All rights reserved.

import { Icon, Shimmer, ShimmerElementType } from '@fluentui/react';
import { getCheckoutSession, TierOption } from 'billing/BillingService';
import { DgButton, DgFlexCenteredRow } from 'components/common/DgCommonStyledComponents';
import { dgFontFamily, LargeHeading, SmallContent } from 'components/common/Fonts';
import { ExplorerTextButton } from 'components/myContentExplorer/ExplorerStyles';
import { homeGray, landingPageBackground, primaryTheme } from 'components/shell/presentation/AppStyles';
import { Config } from 'config';
import { useBillingTiers } from 'hooks/BillingTiers';
import { useDgAuth } from 'hooks/DgAuthentication';
import { NavigationType, useNavigationService } from 'hooks/navigationService';
import { SubscriptionType } from 'networking/OrganizationApi';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from 'store';
import styled, { css } from 'styled-components';
import { TelemetryPageView } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';

export const promotedSubscriptionLevel = SubscriptionType.Business;
export const customSubscriptionLevel = SubscriptionType.Enterprise;

const smallScreenLimit = 1040;

const LandingPage = styled.div`
    display: flex;
    flex: 1;
    padding: 20px;
    box-sizing: border-box;
    justify-content: center;
    background-color: ${landingPageBackground};
    overflow: auto;
`;

const PageContainer = styled.div<{smallScreen: boolean}>`
    display: flex;
    flex-direction: column;
    width: 100%;
    text-align: center;
    align-items: center;
    gap: 4vh;
    ${LargeHeading} {
        ${props => props.smallScreen && 'font-size: 20px'};
    }
`;

const ButtonsContainer = styled.div<{alignCenter: boolean}>`
    display: flex;
    flex-direction: row;
    align-self: ${props => props.alignCenter ? 'center' : 'flex-end'};
    align-items: center;
    gap: 30px;
    padding-bottom: 20px;
`;

const SubheadingText = styled(SmallContent)<{smallScreen: boolean}>`
    text-align: center;
    ${props => props.smallScreen && 'font-size: 16px'};
`;

export const BillingPage = (): JSX.Element => {
    const {user} = useDgAuth();
    const userFirstName = user?.given_name;
    const organization = useSelector((state: AppState) => state.organizations.currentOrganization);
    const navService = useNavigationService();
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        window.addEventListener("resize", () => setWindowWidth(window.innerWidth));
        return () => window.removeEventListener("resize", () => setWindowWidth(window.innerWidth));
    });

    useEffect(() => {
        Telemetry.trackPageView(TelemetryPageView.Tiers);
    }, []);

    return (
        <LandingPage>
            <PageContainer smallScreen={windowWidth < smallScreenLimit}>
                <LargeHeading>Welcome, {userFirstName} @ {organization?.name}!</LargeHeading>
                <SubheadingText smallScreen={windowWidth < smallScreenLimit}>Please choose a subscription. You can upgrade or cancel at any time.</SubheadingText>
                <TiersView onDismiss={() => navService.navToHomePage(NavigationType.PushNewPage)} />
            </PageContainer>
        </LandingPage>
    );
}

const SmallTilesContainerCss = css`
    flex-direction: column;
    align-items: center;
    margin-top: 70px;
    margin-bottom: 30px;
`;

const TilesContainer = styled.div<{smallScreen: boolean, screenSize: number}>`
    display: flex;
    justify-content: center;
    flex-direction: row;
    align-items: align-start;
    align-self: center;
    width: ${props => props.smallScreen && props.screenSize > 610 ? '530px' : '100%'};
    padding: 10px;
    gap: 3%;

    ${props => !!props.smallScreen && SmallTilesContainerCss}
`;

const TiersViewContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
`;

interface TiersViewProps {
    onDismiss: () => void;
    readOnly?: boolean;
}

export const TiersView = (props: TiersViewProps): JSX.Element => {
    const { tiers, isLoading } = useBillingTiers();
    const [selectedTierType, setSelectedTierType] = useState<SubscriptionType | null>(null);
    const organization = useSelector((state: AppState) => state.organizations.currentOrganization);
    const currentSubscriptionType = useSelector((state: AppState) => state.organizations.organizationAccount?.subscription.subscriptionType);

    const isPromotedTier = (tier: TierOption) => tier.type === promotedSubscriptionLevel;

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        window.addEventListener("resize", () => setWindowWidth(window.innerWidth));
        return () => window.removeEventListener("resize", () => setWindowWidth(window.innerWidth));
    });

    useEffect(() => {
        if (!!tiers && tiers.length > 0) {
            let selectedSubscriptionType: SubscriptionType;
            // If there is a current non-trial subscription, show that as the selected tier
            if (!!currentSubscriptionType && currentSubscriptionType !== SubscriptionType.Trial && !!tiers.find(x => x.type === currentSubscriptionType)) {
                selectedSubscriptionType = currentSubscriptionType;
            } else {
                // Use promoted subscription tier as default selected, or fallback to the first item in the list
                selectedSubscriptionType = tiers.find(isPromotedTier)?.type || tiers[0].type;
            }
            setSelectedTierType(selectedSubscriptionType);
        }
    }, [tiers, currentSubscriptionType])

    function onAdvance() {
        if (!selectedTierType) {
            return;
        }

        const selectedTier = tiers?.find(tier => tier.type === selectedTierType);
        if (!selectedTier?.priceId) {
            // If there is no priceId, open the contact us page
            window.open(Config.contactUsUrl, '_blank');
            return;
        }

        getCheckoutSession(organization.id, selectedTier.priceId);
    }

    function getLoadingDisplay() {
        return (
            <>
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 550, width: 240 }]} />
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 550, width: 240 }]} />
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 550, width: 240 }]} />
                <Shimmer shimmerElements={[{ type: ShimmerElementType.line, height: 550, width: 240 }]} />
            </>
        )
    }

    return (
        <TiersViewContainer>
            <TilesContainer smallScreen={windowWidth < smallScreenLimit} screenSize={windowWidth}>
                {isLoading ? getLoadingDisplay() : tiers.map(tier => {
                    const tierIsDisabled = tier.type === SubscriptionType.Trial;
                    const tierIsSelectable = selectedTierType !== tier.type && !tierIsDisabled;
                    let selectionProps = null;
                    if (!props.readOnly) {
                        selectionProps = {
                            onClick: tierIsSelectable ? () => setSelectedTierType(tier.type) : undefined,
                            isSelected: selectedTierType === tier.type,
                            isSelectable: tierIsSelectable
                        }
                    }
                    return (
                        <InfoTileContainer key={tier.priceId} onClick={selectionProps?.onClick} selected={selectionProps?.isSelected} selectable={selectionProps?.isSelectable}>
                            <TierInfoTile
                                type={tier.type}
                                title={tier.name}
                                price={tier.price}
                                priceId={tier.priceId}
                                description={tier.description}
                                isPromoted={isPromotedTier(tier)}
                                disabled={tierIsDisabled}
                            />
                        </InfoTileContainer>
                    )
                })}
            </TilesContainer>
            {windowWidth < smallScreenLimit && <br />}
            <ButtonsContainer alignCenter={windowWidth < smallScreenLimit}>
                <ExplorerTextButton onClick={props.onDismiss}>Cancel</ExplorerTextButton>
                {!props.readOnly && <DgButton disabled={!selectedTierType} onClick={onAdvance}>Next</DgButton>}
            </ButtonsContainer>
        </TiersViewContainer>
    )
}

const SelectedCss = css`
    transform: scale(1.05);
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.15);
`;

const SelectableCss = css`
    cursor: pointer;
    :hover {
        transform: scale(1.03);
        box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.1);
    }
`;

const InfoTileContainer = styled.div<{ selected?: boolean, selectable?: boolean }>`
    height: 550px;
    flex-basis: 240px;
    flex-grow: 1;
    box-shadow: 0 11px 30px rgba(0, 0, 0, 0.05);
    border: 0.5px solid rgba(100,146,253,.2);
    border-radius: 9px;
    display: flex;
    flex-direction: column;
    align-items: center;
    box-sizing: border-box;
    transition: all 0.1s ease-in-out;
    background-color: ${primaryTheme.palette.white};
    padding: 36px 32px 10px;
    position: relative;

    ${props => !!props.selectable && SelectableCss};
    ${props => !!props.selected && SelectedCss}
`;

const InfoContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 15px;
    overflow: hidden;
    text-align: center;
    width: 100%;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    flex: 1;
    padding-top: 10px;
    justify-content: space-evenly;
    text-align: left;
`;

const CenteredContentContainer = styled(ContentContainer)`
    text-align: center;
`;

const HeadingText = styled.span<{disabled?: boolean}>`
    font-family: 'Montserrat';
    font-style: normal;
    font-weight: bold;
    font-size: 19px;
    line-height: 26px;
    text-align: center;
    color: ${homeGray};
`;

const Header = styled(HeadingText)`
    border-radius: 16px 16px 0px 0px;
`;

const Footer = styled(HeadingText)`
    font-size: 16px;
    height: 30px;
`;

const PricesContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-bottom: 20px;
    border-bottom: 1px solid #f3f3f3;
    gap: 15px;
`;

const PriceText = styled.span`
    font-family: ${dgFontFamily};
    font-weight: 600;
    font-size: 25px;
    line-height: 40px;
    color: ${homeGray};
`;

const PricePerText = styled(PriceText)`
    font-size: 17px;
    line-height: 25px;
`;

const DescriptionText = styled(PriceText)`
    font-size: 15px;
    line-height: 25px;
    font-family: 'Roboto';
`;

const DescriptionIcon = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    /* height should be the same as the line height of the description text */
    height: 25px;
`;

const DescriptionContainer = styled(DgFlexCenteredRow)`
    gap: 10px;
`;

interface TierInfoTitleProps {
    type: SubscriptionType;
    title: string;
    price: number;
    priceId: string;
    description: string[];
    isPromoted?: boolean;
    disabled?: boolean;
}

const TierInfoTile = (props: TierInfoTitleProps): JSX.Element => {
    const isCustomTile = !props.priceId && (props.type === SubscriptionType.Trial || props.type === customSubscriptionLevel);
    if (isCustomTile) {
        return <CustomInfoTile {...props} />
    }

    function getPriceText() {
        return (
            <PricesContainer>
                <div>
                    <PriceText>${props.price.toLocaleString()}</PriceText>
                    <PricePerText style={{ marginLeft: '10px' }}>/mo</PricePerText>
                </div>
            </PricesContainer>
        )
    }

    return (
        <InfoContentContainer>
            <Header disabled={props.disabled}>{props.title}</Header>
            {getPriceText()}
            <ContentContainer>
                {props.description.map((desc, index) => (
                    <DescriptionContainer key={`desc_line_${index}`}>
                        <DescriptionIcon>
                            <Icon iconName='Completed' styles={{ root: { color: primaryTheme.palette.themePrimary }}} />
                        </DescriptionIcon>
                        <DescriptionText>{desc}</DescriptionText>
                    </DescriptionContainer>
                ))}
            </ContentContainer>
            <Footer>{props.isPromoted ? 'MOST POPULAR' : ''}</Footer>
        </InfoContentContainer>
    )
}

const CustomInfoTile = (props: TierInfoTitleProps): JSX.Element => {
    return (
        <InfoContentContainer>
            <Header disabled={props.disabled}>{props.title}</Header>
            <CenteredContentContainer>
                {props.description.map((desc, index) => <DescriptionText key={`desc_line_${index}`}>{desc}</DescriptionText>)}
            </CenteredContentContainer>
            <Footer/>
        </InfoContentContainer>
    )
}
